import { imageSpaceAPI } from '@/utils/api';

/** 图片空间列表默认每页条数（后端单页上限仍为 LIST_PAGE_SIZE=200） */
export const IMAGE_SPACE_PAGE_SIZE = 50;

/** 与后端 AdminImageSpaceController.LIST_PAGE_SIZE 上限对齐 */
export const IMAGE_SPACE_MAX_PAGE_SIZE = 200;

/** 加载更多单次触发最多连续请求数（仅跳过滤空页，禁止单次触发连环扫库） */
export const IMAGE_SPACE_LOAD_MORE_MAX_SKIPS = 2;

/**
 * @deprecated 服务端已按 deletedStatus=active|trash 分页凑页；保留常量避免外部引用断裂。
 */
export const IMAGE_SPACE_ACTIVE_SEEK_MAX_SKIPS = 2;

/** 未进回收站的记录（主列表 / 全部 Tab 可见） */
export function isImageSpaceListActiveRow(item) {
  if (!item || typeof item !== 'object') return false;
  const deletedAt = item.deletedAt;
  if (deletedAt == null) return true;
  const text = String(deletedAt).trim();
  return !text || text === 'null' || text === 'undefined';
}

export function countImageSpaceActiveRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(isImageSpaceListActiveRow).length;
}

/**
 * @param {object} [filters]
 * @param {'active'|'trash'|'all'} [filters.deletedStatus] 默认 active（主列表）
 */
export async function fetchImageSpacePage(page, pageSize = IMAGE_SPACE_PAGE_SIZE, filters = {}) {
  const query = { page, pageSize };
  const startDate = String(filters.startDate || '').trim();
  const endDate = String(filters.endDate || '').trim();
  const ownerUserId = String(filters.ownerUserId || filters.ownerId || '').trim();
  const source = String(filters.source || '').trim();
  const deletedStatus = String(filters.deletedStatus || 'active').trim().toLowerCase() || 'active';
  if (startDate) query.startDate = startDate;
  if (endDate) query.endDate = endDate;
  if (ownerUserId) query.ownerUserId = ownerUserId;
  if (source) query.source = source;
  if (deletedStatus) query.deletedStatus = deletedStatus;
  return imageSpaceAPI.getAll(query);
}

/**
 * 统一解析图片空间分页响应。
 * 严格信任后端 hasMore；仅在完全缺失时用 page*pageSize < total 推断。
 * 禁止「满页就乐观 hasMore」——那会在视口贴底时自动拉完全库打爆服务端。
 */
export function normalizeImageSpacePage(res) {
  const nested = res?.data && typeof res.data === 'object' && !Array.isArray(res.data)
    ? res.data
    : null;
  const rows = Array.isArray(res?.images)
    ? res.images
    : (Array.isArray(nested?.images) ? nested.images : []);

  const page = Number(res?.page ?? nested?.page) || 1;
  const pageSize = Number(res?.pageSize ?? nested?.pageSize) || IMAGE_SPACE_PAGE_SIZE;
  const rawTotal = res?.total ?? nested?.total;
  const totalNum = Number(rawTotal);
  const totalProvided = rawTotal != null && rawTotal !== '' && Number.isFinite(totalNum) && totalNum >= 0;

  let safeTotal;
  if (totalProvided && totalNum > 0) {
    safeTotal = totalNum;
  } else if (totalProvided && totalNum === 0 && rows.length > 0) {
    // total=0 但有数据：只信本页条数，不据此推断还有更多
    safeTotal = rows.length;
  } else if (totalProvided) {
    safeTotal = 0;
  } else {
    safeTotal = rows.length;
  }

  const rawHasMore = res?.hasMore ?? nested?.hasMore;
  let hasMore;
  if (typeof rawHasMore === 'boolean') {
    hasMore = rawHasMore;
  } else if (totalProvided && safeTotal > 0) {
    hasMore = page * pageSize < safeTotal;
  } else {
    // 无 hasMore、无可靠 total：仅当满页时谨慎认为可能还有，由下一页空结果收口
    hasMore = rows.length >= pageSize;
  }

  // 无可靠 hasMore 时，用 total 校正（不反向把后端 false 改 true）
  if (!hasMore && typeof rawHasMore !== 'boolean' && safeTotal > page * pageSize) {
    hasMore = true;
  }

  return { rows, total: safeTotal, hasMore, page, pageSize };
}

export function appendUniqueImageSpaceRows(prev, rows) {
  const list = Array.isArray(prev) ? prev : [];
  const incoming = Array.isArray(rows) ? rows : [];
  if (!incoming.length) return list;
  const seen = new Set(list.map((item) => String(item?.id || '')).filter(Boolean));
  const appended = [];
  incoming.forEach((item) => {
    const id = item?.id != null ? String(item.id) : '';
    if (!id || seen.has(id)) return;
    seen.add(id);
    appended.push(item);
  });
  if (!appended.length) return list;
  return [...list, ...appended];
}

/**
 * 分页合并拉取全量图片记录，供容量统计等场景使用；单页上限与后端 LIST_PAGE_SIZE 对齐。
 *
 * @param {object} [filters] 列表筛选条件
 * @param {{ maxPages?: number }} [options] maxPages 最大请求页数，默认 200，上限 500
 * @returns {Promise<object[]>} 合并后的图片行数组
 */
export async function fetchAllImageSpaceRowsForStorage(filters = {}, { maxPages = 200 } = {}) {
  const merged = [];
  let page = 1;
  let totalPages = 1;
  const safeMaxPages = Math.max(1, Math.min(maxPages, 500));

  while (page <= totalPages && page <= safeMaxPages) {
    const res = await fetchImageSpacePage(page, IMAGE_SPACE_MAX_PAGE_SIZE, {
      ...filters,
      deletedStatus: filters.deletedStatus || 'all',
    });
    const normalized = normalizeImageSpacePage(res);
    totalPages = Math.max(
      totalPages,
      Math.ceil((normalized.total || 0) / IMAGE_SPACE_MAX_PAGE_SIZE) || 1,
    );
    if (normalized.rows.length) {
      merged.push(...normalized.rows);
    }
    if (!normalized.hasMore && normalized.rows.length < IMAGE_SPACE_MAX_PAGE_SIZE) {
      break;
    }
    page += 1;
  }

  return merged;
}

/** 展示用总数：服务端 total 无效时回退到已加载数 */
export function resolveImageSpaceCountLabel(listTotal, loadedCount = 0) {
  const loaded = Math.max(0, Number(loadedCount) || 0);
  if (listTotal == null || listTotal === '') return loaded;
  const parsed = Number(listTotal);
  if (!Number.isFinite(parsed) || parsed < 0) return loaded;
  if (parsed === 0) return loaded;
  return Math.max(parsed, loaded);
}
